import {FormModel} from "../../../../models/forms/form.model";
import {IPersonFormFields} from "./person-form-fields.interface";
import {FormFields} from "../../../../models/forms/form-field.interface";
import {IDotRezPerson, IDotRezPersonPhoneNumber} from "../../../dot-rez-api/data-contracts/user/person.data-contracts";
import {NullableDate, NullableString} from "../../../../types/nullable-types";
import {IPersonViewModel} from "./person-view-model.interface";
import {IPersonTravelDocumentViewModel} from "./person-travel-document-view-model.interface";
import {Check} from "../../../../types/type-checking";
import {BirthdateValidator} from "../../../../models/forms/field-validators/birthdate.validator";
import {PhoneTypeEnum} from "../../../dot-rez-api/data-contracts/enums/phone-type.enum";
import {genderToNumber, NullableGender} from "../../../dot-rez-api/data-contracts/enums/gender.enum";

const MAX_TRAVEL_DOCUMENTS_COUNT = 4;
export abstract class PersonModel extends FormModel<IPersonFormFields> implements IPersonViewModel {

    abstract get personData(): IDotRezPerson;
    protected abstract get isPhoneNumberRequired(): boolean;
    protected abstract _createNewTravelDocument(): IPersonTravelDocumentViewModel;
    protected abstract _getPersonDetailsNotCompletedErrorMessage(): string;
    abstract get travelDocuments(): IPersonTravelDocumentViewModel[];

    protected _createFields(): FormFields<IPersonFormFields> {
        const language = this.services.language;

        return {
            firstName: this._createFirstNameField({
                initialValue: () => this.firstName
            }),
            lastName: this._createLastNameField({
                initialValue: () => this.lastName
            }),
            dateOfBirth: this._createField<NullableDate>({
                fieldName: () => language.translate('Date of birth'),
                initialValue: () => this.dateOfBirth,
                validators: [
                    new BirthdateValidator(this.services)
                ]
            }),
            gender: this._createGenderField({
                //When a person is created in dotREZ without specifying the gender then that person gets by default Male as gender
                //We don't want to use the default value Male for any person.
                //So here if the person has dateOfBirth filled in it means that also gender was filled in by the user.
                //If the dateOfBirth is missing it means that this.personData.details.gender got the default Male value and we want in this case the gender to be null
                initialValue: () => genderToNumber(this.gender)
            }),
            residentCountry: this._createField<NullableString>({
                fieldName: () => language.translate('Resident Country'),
                initialValue: () => this.residentCountry,
                isRequired: false
            }),
            nationality: this._createField<NullableString>({
                fieldName: () => language.translate('Citizenship'),
                initialValue: () => this.nationality,
                maxLength: 2
            }),
            emailAddress: this._createEmailField({
                fieldName: () => language.translate('Email Address'),
                initialValue: () => this.emailAddress,
            }),
            workPhoneNumber: this._createPhoneField({
                fieldName: () => language.translate('Work Phone number'),
                initialValue: () => this.workDotRezPhoneNumber?.number ?? null,
                isRequired: this.isPhoneNumberRequired
            }),
            phoneNumber: this._createPhoneField({
                fieldName: () => language.translate('Home phone number'),
                initialValue: () => this.homeDotRezPhoneNumber?.number ?? null,
                isRequired: false
            })
        };
    }
    get title(): NullableString {
        if(Check.isUndefined(this.personData.name.title)) {
            return null;
        }
        return this.personData.name.title;
    }

    get customerNumber(): string {
        return this.personData.customerNumber;
    }

    get emailAddress(): string {
        return this.personData.emailAddresses[0]?.email || "";
    }

    get firstName(): string {
        return this.personData.name.first ?? "";
    }

    get lastName(): string {
        return this.personData.name.last  ?? "";
    }

    get dateOfBirth(): NullableDate {
        return this.services.time.tryConvertToDate(this.personData.details.dateOfBirth);
    }

    getFullName(): string {
        return `${this.firstName} ${this.lastName}`;
    }

    getInitials(): string {
        const firstName = this.firstName || "?";
        const lastName =this.lastName || "?";

        return firstName[0].toUpperCase() + lastName[0].toUpperCase();
    }

    get nationality(): NullableString {
        return this.personData.details.nationality;
    }

    get residentCountry(): NullableString{
        return this.personData.details.residentCountry;
    }

    get gender(): NullableGender {
        return this.dateOfBirth ? this.personData.details.gender : null;
    }

    get workDotRezPhoneNumber(): IDotRezPersonPhoneNumber | null {
        return this.personData.phoneNumbers.findFirst( phone => {
            return phone.type === PhoneTypeEnum.Work
        });
    }

    get homeDotRezPhoneNumber(): IDotRezPersonPhoneNumber | null {
        return this.personData.phoneNumbers.findFirst( phone => {
            return phone.type === PhoneTypeEnum.Home
        }) ;
    }


    async addTravelDocument(): Promise<void> {
        if(this.travelDocuments.length >= MAX_TRAVEL_DOCUMENTS_COUNT) {
            this.services.alert.showError(this.services.language.translationFor('Maximum number of allowed documents is {maxNumberOfDocuments}. You have reached this number.')
                .withParams({maxNumberOfDocuments: MAX_TRAVEL_DOCUMENTS_COUNT}))
            return;
        }
        if(this.hasErrors()) {
            this.services.alert.showError(this._getPersonDetailsNotCompletedErrorMessage())
            return;
        }
        const travelDocument = this._createNewTravelDocument();
        await travelDocument.edit();
    }
}
